// @flow
import React, { Component } from 'react';
import styled, { withTheme } from 'styled-components';
import Dropzone from 'react-dropzone';
import { translate } from 'react-i18next';
import RaisedButton from 'material-ui/RaisedButton';
import Card from 'common/components/Card';
import Dialog from 'material-ui/Dialog';
import TextField from 'material-ui/TextField';
import Campaign from 'common/modules/campaign/domain/Campaign';


const TextFieldWrapper = styled.div`
  margin: 2.5%;
`;

const MessageCard = styled(Card)`
  display: flex;
  padding: 20px;
  margin-bottom: 50px;
`;

const Flex = styled.div`
  display: flex;
`;

const MessageSection = styled.div`
  flex: 3;
`;

const AudioPlayerSection = styled.div`
  flex: 1;
`;

const MessageBox = styled.div`
  white-space: pre-line;
  text-align: justify;
`;

const FullWidth = styled.div`
  width: 100%;
`;

const BottomRow = styled.div`
  margin-top: 10px;
  display: flex;
`;

const UploadButtonWrapper = styled.div`
  display: flex;
  flex: 1; 
  justify-content: flex-end;
`;

const EditButtonWrapper = styled.div`
  flex: 3;
  display: flex;  
`;

// This is a fix to reRender Dialog with vertical centering : https://github.com/mui-org/material-ui/issues/2322#issuecomment-163433242
const fixStyle = {
  position: 'absolute',
  left: '50%',
  top: '50%',
  transform: 'translate(-50%, -50%)',
};

type Props = {
  message: string;
  audioFilePath: string;
  uploadAudioFileMessage: (File) => Promise<string>;
  editMessage: (string) => Promise<Campaign>;
  removeAudioFileMessage: () => Promise<Campaign>;
  actions: {
    openSnackbar: (string, string) => mixed;
  },
  t: any;
  theme: any;
}

type State = {
  isEditingCampaignMessage: boolean;
  audioFilePath: ?string,
  campaignMessage: string;
  isConfirmingDeleteAudioFile: boolean,
  error: ?any;
}

class CampaignMessageComponent extends Component<Props, State> {

  state: State = {
    isEditingCampaignMessage: false,
    campaignMessage: '',
    audioFilePath: this.props.audioFilePath,
    error: false,
    isConfirmingDeleteAudioFile: false,
    confirmedDelete: false,
  };

  openEditCampaignMessage = () => {
    this.setState({ isEditingCampaignMessage: true, campaignMessage: this.props.message });
  };

  openDeleteDialog = () => {
    this.setState({ isConfirmingDeleteAudioFile: true });
  };

  closeEditCampaignMessageDialog() {
    this.setState({ isEditingCampaignMessage: false });
  }

  editCampaignMessage = () => {
    this.props.editMessage(this.state.campaignMessage);
    this.closeEditCampaignMessageDialog();
  };

  onCloseEditCampaignMessage = () => {
    this.closeEditCampaignMessageDialog();
  };

  onCloseDeleteAudioFileDialog = () => {
    this.setState({ isConfirmingDeleteAudioFile: false });
  };

  onConfirmDeleteAudioFileDialog = () => {
    this.onCloseDeleteAudioFileDialog();
    this.props.removeAudioFileMessage();
  };

  onCampaignMessageChange = (event) => {
    this.setState({ campaignMessage: event.target.value });
  };

  onAudioMessageFileDrop = (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];

    this.props.uploadAudioFileMessage(file);
  };

  render() {
    const { t, theme, message, audioFilePath } = this.props;

    const closeEditCampaignMessageButton = [
      <RaisedButton primary style={{ margin: '0 10px' }} label={t('close')} onClick={this.onCloseEditCampaignMessage} key="close" />,
      <RaisedButton primary style={{ margin: '0 10px' }} label={t('save')} onClick={this.editCampaignMessage} key="save" />,
    ];

    const closeDeleteAudioFileDialog = [
      <RaisedButton primary style={{ margin: '0 10px' }} label={t('no')} onClick={this.onCloseDeleteAudioFileDialog} key="no" />,
      <RaisedButton primary style={{ margin: '0 10px' }} label={t('yes')} onClick={this.onConfirmDeleteAudioFileDialog} key="yes" />,
    ];

    return (
      <div>
        <MessageCard>
          <FullWidth>
            <Flex>
              <MessageSection>
                <h3>{t('campaignMessageSection')}</h3>
                <Flex>
                  {this.props.message &&
                    <div style={{ margin: '20px' }}>
                      <MessageBox>
                        {this.props.message}
                      </MessageBox>
                    </div>
                  }
                  {!message &&
                    <RaisedButton primary label={t('addCampaignMessageButton')} onClick={this.openEditCampaignMessage} />
                  }
                </Flex>
              </MessageSection>
              <AudioPlayerSection>
                <h3>{t('audioPlayerSection')}</h3>
                <div style={{ margin: 'auto' }}>
                  {audioFilePath &&
                    <audio controls src={audioFilePath} style={{ width: '100%' }} />
                  }
                  {!audioFilePath &&
                    <div>
                      <Dropzone style={{} /* otherwise creates a visual bug */} multiple={false} accept={'audio/*'} onDrop={this.onAudioMessageFileDrop}>
                        <RaisedButton primary label={t('dropZoneAudioFile')} />
                      </Dropzone>
                    </div>
                  }
                </div>
              </AudioPlayerSection>
            </Flex>

            <BottomRow>
              {message &&
                <EditButtonWrapper>
                  <RaisedButton primary label={t('editCampaignMessageButton')} onClick={this.openEditCampaignMessage} />
                </EditButtonWrapper>
              }
              <UploadButtonWrapper>
                {audioFilePath &&
                  <div style={{ display: 'flex' }}>
                    <Dropzone style={{ alignSelf: 'flex-end' }} multiple={false} accept={'audio/*'} onDrop={this.onAudioMessageFileDrop}>
                      <RaisedButton primary label={t('dropZoneAudioFile')} />
                    </Dropzone>
                    <RaisedButton style={{ alignSelf: 'flex-end', marginLeft: '8px' }} primary label={t('delete')} onClick={this.openDeleteDialog} />
                  </div>
                }
              </UploadButtonWrapper>
            </BottomRow>
          </FullWidth>
        </MessageCard>

        <Dialog
          actions={closeEditCampaignMessageButton}
          autoScrollBodyContent
          bodyStyle={{ backgroundColor: theme.colors.primaryBackground }}
          contentStyle={fixStyle}
          title={t('editCampaignMessage')}
          titleStyle={{ fontSize: theme.fonts.style.secondary.fontSize, color: theme.colors.tertiaryText, backgroundColor: theme.colors.primary }}
          open={this.state.isEditingCampaignMessage}
          onRequestClose={this.onCloseEditCampaignMessage}
        >
          <TextFieldWrapper>
            <TextField
              id={'CampaignMessageTextField'}
              key={'CampaignMessageTextField'}
              fullWidth
              rows={3}
              multiLine
              placeholder={t('campaignMessageTextField')}
              onChange={this.onCampaignMessageChange}
              value={this.state.campaignMessage}
            />
          </TextFieldWrapper>
        </Dialog>
        <Dialog
          actions={closeDeleteAudioFileDialog}
          autoScrollBodyContent
          bodyStyle={{ backgroundColor: theme.colors.primaryBackground }}
          contentStyle={fixStyle}
          title={t('deleteAudioFileMessage')}
          titleStyle={{ fontSize: theme.fonts.style.secondary.fontSize, color: theme.colors.tertiaryText, backgroundColor: theme.colors.primary }}
          open={this.state.isConfirmingDeleteAudioFile}
          onRequestClose={this.onCloseEditCampaignMessage}
        />
      </div>
    );
  }
}

export default translate('commonCampaign')(withTheme(CampaignMessageComponent));



// WEBPACK FOOTER //
// ./src/statsradio/modules/campaigns/components/CampaignMessageComponent.js